import { useState } from "react";
import { clearCacheService } from "../../../core/services/lmsServices";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import queryString from "query-string";
import { apis } from "../../../core/services/params";

const LABEL_CLASS = "block text-xs mb-1.5 text-[#5E5D5D] font-medium";
const INPUT_CLASS = "w-full h-9 rounded border text-[#555] border-[#EAEAEA] px-2.5 outline-none text-sm font-light focus:border-blue-400";
const HINT_CLASS = "text-[11px] text-[#858585] font-light mt-1.5";
const PANEL_CLASS = "bg-white shadow-[0_1px_4px_rgba(0,0,0,.2)] rounded flex flex-col";
const PANEL_TITLE_CLASS = "bg-[#f6f8f8] p-4 border-b border-b-[#efeff4]";
const ACTION_BUTTON_CLASS = "bg-blue-500 text-white px-8 rounded h-9 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed";

// Every cache operation is the same shape: a title, one or more identifier
// fields, and a single button that fires a purge.
const CachePanel = (props: {
    title: string,
    description: string,
    children: React.ReactNode,
    actionLabel: string,
    loadingLabel: string,
    isLoading: boolean,
    isDisabled?: boolean,
    onAction: () => void,
    className?: string,
}) => (
    <div className={`${PANEL_CLASS} ${props.className || ""}`}>
        <div className={PANEL_TITLE_CLASS}>
            <p className="text-base text-[#43425D]">{props.title}</p>
            <p className="text-xs text-[#858585] font-light mt-0.5">{props.description}</p>
        </div>
        <div className="py-4 px-2.5 flex flex-col flex-1">
            <div className="px-2 flex-1">{props.children}</div>
            <div className="px-2 pt-4">
                <button
                    className={ACTION_BUTTON_CLASS}
                    onClick={props.onAction}
                    disabled={props.isLoading || props.isDisabled}
                >
                    {props.isLoading ? props.loadingLabel : props.actionLabel}
                </button>
            </div>
        </div>
    </div>
);

const CacheDeletion = () => {
    const { user } = useSelector((s: any) => s.authStore);
    const [selectedDevice, setSelectedDevice] = useState('PWA');
    const [loading, setLoading] = useState(false);
    const [categoryCode, setCategoryCode] = useState('');
    const [msnId, setMsnId] = useState('');
    const [cdnUrl, setCdnUrl] = useState('');
    const [cdnLoading, setCdnLoading] = useState(false);
    const [categoryLoading, setCategoryLoading] = useState(false);
    const [msnLoading, setMsnLoading] = useState(false);
    const [productId, setProductId] = useState('');
    const [productLoading, setProductLoading] = useState(false);
    const [storePageUrl, setStorePageUrl] = useState('');
    const [storePageLoading, setStorePageLoading] = useState(false);

    // Brand and Category Cache states
    const [brandId, setBrandId] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [brandCategoryLoading, setBrandCategoryLoading] = useState(false);

    const clearHomePageCache = async () => {
        setLoading(true);
        const stringifiedQuery = queryString.stringify({
            user: user.id || -1,
            type: selectedDevice
        });
        try {
            await clearCacheService(stringifiedQuery)
            setLoading(false);
            toast.success("Homepage cache cleared successfully!")
        }
        catch (err) {
            setLoading(false);
            toast.error("Failed to clear homepage cache!")
        }
        return
    }

    const clearCategoryCache = async () => {
        if (!categoryCode.trim()) {
            toast.error("Please enter a category code");
            return;
        }
        setCategoryLoading(true);
        try {
            // Add your category cache clearing service call here
            // await clearCategoryCacheService(categoryCode);
            setTimeout(() => {
                setCategoryLoading(false);
                toast.success(`Category cache cleared for: ${categoryCode}`);
                setCategoryCode('');
            }, 1000);
        } catch (err) {
            setCategoryLoading(false);
            toast.error("Failed to clear category cache!");
        }
    }

    const clearMsnCache = async () => {
        if (!msnId.trim()) {
            toast.error("Please enter an MSN ID");
            return;
        }
        setMsnLoading(true);
        try {
            // Add your MSN cache clearing service call here
            // await clearMsnCacheService(msnId);
            setTimeout(() => {
                setMsnLoading(false);
                toast.success(`MSN cache cleared for: ${msnId}`);
                setMsnId('');
            }, 1000);
        } catch (err) {
            setMsnLoading(false);
            toast.error("Failed to clear MSN cache!");
        }
    }

    const clearProductCache = async () => {
        if (!productId.trim()) {
            toast.error("Please enter a Product ID");
            return;
        }
        setProductLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_CMS_SERVICE}/api/platform/purge-cache/product?productIds=${encodeURIComponent(productId)}&userId=${encodeURIComponent(user.id || -1)}`;
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            fetch(`${process.env.REACT_APP_GATEWAY_SERVICE_V2}/aggregate/pdpDetails/clear?msn=${encodeURIComponent(productId)}`, {
                method: 'DELETE'
            }).catch((err) => console.error('pdpDetails cache clear error:', err));

            setProductLoading(false);
            toast.success(`Product cache cleared successfully for: ${productId}`);
            setProductId('');
        } catch (err) {
            setProductLoading(false);
            console.error('Product cache clear error:', err);
            toast.error("Failed to clear product cache!");
        }
    }

    const purgeCdnCache = async () => {
        if (!cdnUrl.trim()) {
            toast.error("Please enter a CDN URL");
            return;
        }

        // Basic URL validation
        try {
            new URL(cdnUrl);
        } catch {
            toast.error("Please enter a valid URL");
            return;
        }

        setCdnLoading(true);
        try {
            const apiUrl = `${process.env.REACT_APP_CMS_SERVICE}/api/cms/purge-cache/fastly?url=${encodeURIComponent(cdnUrl)}&userId=${encodeURIComponent(user.id || -1)}`;

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.responseCode === 200) {
                const content = JSON.parse(result.content);
                setCdnLoading(false);
                toast.success(`CDN cache purged successfully! Purge ID: ${content.id}`);
                setCdnUrl('');
            } else {
                throw new Error(result.responseMessage || 'Failed to purge cache');
            }
        } catch (err) {
            setCdnLoading(false);
            console.error('CDN purge error:', err);
            toast.error("Failed to purge CDN cache!");
        }
    }

    const clearStorePageCache = async () => {
        if (!storePageUrl.trim()) {
            toast.error("Please enter a store page URL");
            return;
        }

        // Basic URL validation
        try {
            new URL(storePageUrl);
        } catch {
            toast.error("Please enter a valid URL");
            return;
        }

        setStorePageLoading(true);
        try {
            const apiUrl = `${apis.PURGE_CACHE_STORE_PAGE}?url=${encodeURIComponent(storePageUrl)}`;

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.status === true || result.responseCode === 200) {
                setStorePageLoading(false);
                toast.success(`Store page cache cleared successfully!`);
                setStorePageUrl('');
            } else {
                throw new Error(result.message || result.responseMessage || 'Failed to clear store page cache');
            }
        } catch (err) {
            setStorePageLoading(false);
            console.error('Store page cache clear error:', err);
            toast.error("Failed to clear store page cache!");
        }
    }

    const clearBrandCategoryCache = async () => {
        if (!brandId.trim() && !categoryId.trim()) {
            toast.error("Please enter either Brand ID or Category ID");
            return;
        }

        setBrandCategoryLoading(true);
        try {
            let apiUrl = `${apis.PURGE_CACHE_BRAND_CATEGORY}?`;

            const params = new URLSearchParams();
            if (categoryId.trim()) {
                params.append('categoryId', categoryId.trim());
            }
            if (brandId.trim()) {
                params.append('brandId', brandId.trim());
            }

            apiUrl += params.toString();

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.status === true || result.responseCode === 200) {
                setBrandCategoryLoading(false);

                const cacheType = categoryId.trim() && brandId.trim()
                    ? 'Brand and Category cache'
                    : categoryId.trim()
                        ? 'Category cache'
                        : 'Brand cache';

                toast.success(`${cacheType} cleared successfully!`);
                setBrandId('');
                setCategoryId('');
            } else {
                throw new Error(result.message || result.responseMessage || 'Failed to clear brand/category cache');
            }
        } catch (err) {
            setBrandCategoryLoading(false);
            console.error('Brand/Category cache clear error:', err);
            toast.error("Failed to clear brand/category cache!");
        }
    }

    return (
        <>
            <div className="p-4 flex items-center">
                <p className="text-xl font-bold text-[#43425D]">CACHE DELETION</p>
            </div>

            <div className="px-4 pb-5">
                <div className="bg-amber-50 border border-amber-200 rounded p-4 mb-5">
                    <p className="text-sm font-medium text-amber-800">Cache Deletion Warning</p>
                    <p className="text-xs text-amber-700 mt-1 font-light">
                        Cache deletion operations are irreversible and may temporarily impact performance.
                        Please ensure you understand the implications before proceeding.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 mb-5">
                    <CachePanel
                        title="Category Cache"
                        description="Clear cache for specific product categories"
                        actionLabel="Clear Cache"
                        loadingLabel="Clearing..."
                        isLoading={categoryLoading}
                        isDisabled={!categoryCode.trim()}
                        onAction={clearCategoryCache}
                    >
                        <label className={LABEL_CLASS}>Category Code:</label>
                        <input
                            className={INPUT_CLASS}
                            type="text"
                            placeholder="e.g., 211196532"
                            value={categoryCode}
                            onChange={(e) => setCategoryCode(e.target.value)}
                        />
                    </CachePanel>

                    <CachePanel
                        title="MSN Cache"
                        description="Clear cache for specific MSN identifiers"
                        actionLabel="Clear Cache"
                        loadingLabel="Clearing..."
                        isLoading={msnLoading}
                        isDisabled={!msnId.trim()}
                        onAction={clearMsnCache}
                    >
                        <label className={LABEL_CLASS}>MSN ID:</label>
                        <input
                            className={INPUT_CLASS}
                            type="text"
                            placeholder="e.g., MSN123456789"
                            value={msnId}
                            onChange={(e) => setMsnId(e.target.value)}
                        />
                    </CachePanel>

                    <CachePanel
                        title="Product Cache"
                        description="Clear cache for specific product identifiers"
                        actionLabel="Clear Cache"
                        loadingLabel="Clearing..."
                        isLoading={productLoading}
                        isDisabled={!productId.trim()}
                        onAction={clearProductCache}
                    >
                        <label className={LABEL_CLASS}>Product ID:</label>
                        <input
                            className={INPUT_CLASS}
                            type="text"
                            placeholder="e.g., msn39yr7234l5q"
                            value={productId}
                            onChange={(e) => setProductId(e.target.value)}
                        />
                    </CachePanel>

                    <CachePanel
                        title="Device Cache"
                        description="Clear homepage cache by device type"
                        actionLabel="Clear Cache"
                        loadingLabel="Clearing..."
                        isLoading={loading}
                        onAction={clearHomePageCache}
                    >
                        <label className={LABEL_CLASS}>Device Type:</label>
                        <select
                            className="bg-white text-xs border text-[#5E5D5D] border-[#EAEAEA] h-9 px-3 rounded w-full outline-none focus:border-blue-400"
                            value={selectedDevice}
                            onChange={(e) => setSelectedDevice(e.target.value)}
                        >
                            <option value="PWA">PWA (Progressive Web App)</option>
                            <option value="APP">Mobile Application</option>
                            <option value="DESKTOP">Desktop Website</option>
                        </select>
                    </CachePanel>

                    <CachePanel
                        title="CDN Cache Purge"
                        description="Purge content from CDN edge servers"
                        actionLabel="Purge Cache"
                        loadingLabel="Purging..."
                        isLoading={cdnLoading}
                        isDisabled={!cdnUrl.trim()}
                        onAction={purgeCdnCache}
                    >
                        <label className={LABEL_CLASS}>URL:</label>
                        <input
                            className={INPUT_CLASS}
                            type="url"
                            placeholder="https://www.moglix.com/electricals/wires-cables/211196532"
                            value={cdnUrl}
                            onChange={(e) => setCdnUrl(e.target.value)}
                        />
                        <p className={HINT_CLASS}>Enter the full URL to purge from edge servers. Propagation takes 5-15 minutes.</p>
                    </CachePanel>

                    <CachePanel
                        title="Store Page Cache"
                        description="Clear cache for specific store pages"
                        actionLabel="Clear Cache"
                        loadingLabel="Clearing..."
                        isLoading={storePageLoading}
                        isDisabled={!storePageUrl.trim()}
                        onAction={clearStorePageCache}
                    >
                        <label className={LABEL_CLASS}>Store Page URL:</label>
                        <input
                            className={INPUT_CLASS}
                            type="url"
                            placeholder="https://www.moglix.com/store/next-day-delivery"
                            value={storePageUrl}
                            onChange={(e) => setStorePageUrl(e.target.value)}
                        />
                        <p className={HINT_CLASS}>Enter the full store page URL to clear its cache.</p>
                    </CachePanel>
                </div>

                <div className={`${PANEL_CLASS} mb-5`}>
                    <div className={PANEL_TITLE_CLASS}>
                        <p className="text-base text-[#43425D]">Brand &amp; Category Cache</p>
                        <p className="text-xs text-[#858585] font-light mt-0.5">Clear cached search results for specific brands or categories</p>
                    </div>
                    <div className="py-4 px-2.5">
                        <div className="flex w-full flex-wrap">
                            <div className="w-full md:w-1/2 px-2 mb-4 md:mb-0">
                                <label htmlFor="brandId" className={LABEL_CLASS}>Brand ID:</label>
                                <input
                                    type="text"
                                    id="brandId"
                                    value={brandId}
                                    onChange={(e) => setBrandId(e.target.value)}
                                    placeholder="e.g., 201"
                                    className={INPUT_CLASS}
                                />
                            </div>
                            <div className="w-full md:w-1/2 px-2">
                                <label htmlFor="categoryId" className={LABEL_CLASS}>Category ID:</label>
                                <input
                                    type="text"
                                    id="categoryId"
                                    value={categoryId}
                                    onChange={(e) => setCategoryId(e.target.value)}
                                    placeholder="e.g., 101"
                                    className={INPUT_CLASS}
                                />
                            </div>
                        </div>
                        <p className={`px-2 ${HINT_CLASS}`}>Enter a Brand ID, a Category ID, or both — at least one is required.</p>
                        <div className="px-2 pt-4">
                            <button
                                onClick={clearBrandCategoryCache}
                                disabled={brandCategoryLoading || (!brandId.trim() && !categoryId.trim())}
                                className={ACTION_BUTTON_CLASS}
                            >
                                {brandCategoryLoading ? "Clearing..." : "Clear Cache"}
                            </button>
                        </div>
                    </div>
                </div>

                <div className={PANEL_CLASS}>
                    <div className={PANEL_TITLE_CLASS}>
                        <p className="text-base text-[#43425D]">Cache Management Information</p>
                    </div>
                    <div className="py-4 px-2.5">
                        <div className="flex w-full flex-wrap">
                            <div className="w-full md:w-1/2 px-2 mb-4 md:mb-0">
                                <p className="text-xs font-medium text-[#5E5D5D] mb-2">Cache Types</p>
                                <ul className="text-xs text-[#555] font-light space-y-1.5">
                                    <li><span className="font-medium">Category Cache:</span> Product category data and listings</li>
                                    <li><span className="font-medium">MSN Cache:</span> Product-specific information and metadata</li>
                                    <li><span className="font-medium">Product Cache:</span> Individual product data and details</li>
                                    <li><span className="font-medium">Device Cache:</span> Homepage content optimized per platform</li>
                                    <li><span className="font-medium">CDN Cache:</span> Static assets cached on edge servers globally</li>
                                    <li><span className="font-medium">Store Page Cache:</span> Cached data for specific store pages</li>
                                    <li><span className="font-medium">Brand &amp; Category Cache:</span> Cached search results for brands and categories</li>
                                </ul>
                            </div>
                            <div className="w-full md:w-1/2 px-2">
                                <p className="text-xs font-medium text-[#5E5D5D] mb-2">Best Practices</p>
                                <ul className="text-xs text-[#555] font-light space-y-1.5">
                                    <li>Clear cache during low-traffic hours when possible</li>
                                    <li>Test functionality after cache clearing operations</li>
                                    <li>Monitor system performance post-cache deletion</li>
                                    <li>Keep cache clearing operations logged for audit trails</li>
                                    <li>CDN purging may take 5-15 minutes to propagate globally</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
export default CacheDeletion;
